'use client'

import { NavigationQueryResult } from '@repo/sanity/types'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@repo/ui/components/accordion'
import { Button } from '@repo/ui/components/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import { cn } from '@repo/ui/lib/utils'
import { FunctionComponent } from 'react'
import useLocaleChange from '../../../hooks/useLocaleChange.hook'
import useLocalePrefetch from '../../../hooks/useLocalePrefetch.hook'
import { useCart } from '../../../providers/CartProvider'
import { useNavigation } from '../../../providers/NavigationProvider'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { useWishlist } from '../../../providers/WishlistProvider'
import { PageDto } from '../../../types/page.types'
import LocaleButtons from '../LocaleButtonGroup'
import AccordionLinkLoop from './AccordionLinkLoop'

interface Props {
  pageDto: PageDto
  navigation: NavigationQueryResult
  className?: string
}

const MobileMenuSheet: FunctionComponent<Props> = ({ pageDto, navigation, className }) => {
  const { cart } = useCart()
  const { wishlist } = useWishlist()
  const { shouldUseInverse } = useNavigation()
  const { openSheet, isSheetOpen, closeSheet } = useSheet()
  const { prefetch } = useLocalePrefetch(pageDto.params.locale)
  const { changeLocale } = useLocaleChange(pageDto.params.locale)

  const onNavigate = () => {
    closeSheet()
  }

  return (
    <>
      <li>
        <Button variant={shouldUseInverse(navigation) ? 'navigationInverse' : 'navigationDefault'} className={cn(className)} onClick={() => openSheet(SHEET.MENU)}>
          {pageDto.dictionary?.navigation?.menu || 'Menu'}
        </Button>
      </li>
      <Sheet open={isSheetOpen(SHEET.MENU)} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent side='right'>
          <SheetHeader>
            <SheetTitle>
              <button type='button' className='uppercase' onClick={() => openSheet(SHEET.WISHLIST)}>
                {`${pageDto.dictionary?.navigation?.wishlist || 'Wishlist'}: ${wishlist.length}`}
              </button>
              <button type='button' className='uppercase' onClick={() => openSheet(SHEET.CART)}>
                {`${pageDto.dictionary?.navigation?.bag || 'Bag'}: ${cart?.totalQuantity || 0}`}
              </button>
            </SheetTitle>
            <SheetDescription className='sr-only'>{pageDto.dictionary?.navigation?.menu || 'Menu'}</SheetDescription>
          </SheetHeader>
          <Accordion type='single' collapsible={true} className='w-full' defaultValue='shop'>
            <AccordionItem value='search' variant='defaultStatic' rounded={false}>
              <AccordionTrigger onClick={() => openSheet(SHEET.SEARCH)}>{pageDto.dictionary?.navigation?.search || 'Search'}</AccordionTrigger>
            </AccordionItem>
            {(navigation?.primaryLinks || []).length > 0 && (
              <AccordionItem value='shop' variant='lightStatic' rounded={false}>
                <AccordionTrigger>{pageDto.dictionary?.navigation?.shop || 'Shop'}</AccordionTrigger>
                <AccordionContent className='p-0'>
                  <Accordion type='single' collapsible={true} className='w-full'>
                    <AccordionLinkLoop locale={pageDto.params.locale} links={navigation?.primaryLinks || []} level={1} onNavigate={onNavigate} />
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            )}
            {(navigation?.secondaryLinks || []).length > 0 && <AccordionLinkLoop locale={pageDto.params.locale} links={navigation?.secondaryLinks || []} onNavigate={onNavigate} />}
            {(navigation?.supportLinks || []).length > 0 && (
              <AccordionItem value='support' variant='lightStatic' rounded={false}>
                <AccordionTrigger>{pageDto.dictionary?.navigation?.customerhelp || 'Customer Help'}</AccordionTrigger>
                <AccordionContent className='p-0'>
                  <Accordion type='single' collapsible={true} className='w-full'>
                    <AccordionItem value='locale' variant='lightStatic' rounded={false}>
                      <AccordionTrigger onClick={prefetch} className='pl-[1.5rem]'>
                        {pageDto.dictionary?.navigation?.currency || 'Currency:'} {pageDto.marketInstance.currency}
                      </AccordionTrigger>
                      <AccordionContent className='pl-[1.5rem]'>
                        <LocaleButtons
                          className='flex-row gap-0'
                          locale={pageDto.params.locale}
                          onChange={(newLocale) => changeLocale(newLocale)}
                          buttonVariantCurrentLocale='defaultStatic'
                          buttonVariantOtherLocale='secondaryStatic'
                        />
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionLinkLoop locale={pageDto.params.locale} links={navigation?.supportLinks || []} level={1} onNavigate={onNavigate} />
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </SheetContent>
      </Sheet>
    </>
  )
}

export default MobileMenuSheet
